import React, {useEffect, useMemo, useRef, useState} from 'react'
import {MapContainer, TileLayer, useMap} from 'react-leaflet'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import {
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Popover,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import FilterListIcon from '@mui/icons-material/FilterList'
import {useTranslation} from 'react-i18next'
import dayjs from 'dayjs'
import 'dayjs/locale/de'
import {SalesPersonMapCustomer, SalesPersonMapData, SalesPersonMapEntry} from '../api/salesperson'
import InCareNetHFService from '../services/InCareNetHFService'

// One identity colour per salesperson — same colour shown on map blobs and table row
const SP_COLORS = [
  '#1565c0', '#b71c1c', '#2e7d32', '#e65100',
  '#4a148c', '#006064', '#880e4f', '#33691e',
]

// Bucket id/colour used for every point when "group by salesperson" is switched off
const ALL_BUCKET_ID = '__all__'
const ALL_BUCKET_COLOR = '#546e7a'

const IMPLANT_MANUFACTURERS = InCareNetHFService.deviceCountManufacturers

export type ViewMode = 'patients' | 'tmz' | 'extSensors' | 'implants'

type HeatPoint = {
  lat: number
  lng: number
  count: number
  spId: string
  customerName: string
  city: string
  postalCode: string
  patientCount: number
  tmzCount: number
  extSensorsCount: number
  implantCount: number
}

// ─── Metric helpers ────────────────────────────────────────────────────────────

function metricForCustomer(c: SalesPersonMapCustomer, mode: ViewMode, manufacturers: string[]): number {
  switch (mode) {
    case 'tmz': return c.tmzCount
    case 'extSensors': return c.extSensorsCount
    case 'implants':
      // Use the server-deduplicated total when all manufacturers are selected; otherwise compute
      // the union client-side from the per-manufacturer patient-ID arrays so a patient with
      // implants from more than one selected manufacturer is counted exactly once.
      if (manufacturers.length === IMPLANT_MANUFACTURERS.length) return c.implantCount
      return new Set(manufacturers.flatMap(mfr => c.implantsByManufacturer[mfr] ?? [])).size
    default: return c.patientCount
  }
}

function metricForEntry(sp: SalesPersonMapEntry, mode: ViewMode, manufacturers: string[]): number {
  switch (mode) {
    case 'tmz': return sp.tmzCount
    case 'extSensors': return sp.extSensorsCount
    case 'implants':
      if (manufacturers.length === IMPLANT_MANUFACTURERS.length) return sp.implantCount
      return sp.customers.reduce((s, c) => s + metricForCustomer(c, 'implants', manufacturers), 0)
    default: return sp.patientCount
  }
}

// ─── Convex hull (Andrew's monotone chain) — used by the "net" view ───────────

type Pt = [number, number] // [lat, lng]

function cross(O: Pt, A: Pt, B: Pt): number {
  return (A[0] - O[0]) * (B[1] - O[1]) - (A[1] - O[1]) * (B[0] - O[0])
}

function convexHull(pts: Pt[]): Pt[] {
  if (pts.length < 2) return pts
  const sorted = [...pts].sort((a, b) => a[0] !== b[0] ? a[0] - b[0] : a[1] - b[1])
  const lower: Pt[] = []
  for (const p of sorted) {
    while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop()
    lower.push(p)
  }
  const upper: Pt[] = []
  for (let i = sorted.length - 1; i >= 0; i--) {
    const p = sorted[i]
    while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop()
    upper.push(p)
  }
  lower.pop()
  upper.pop()
  return [...lower, ...upper]
}

// Simple colour darkening for hover emphasis
function darken(hex: string, amount = 0.25): string {
  const num = parseInt(hex.replace('#', ''), 16)
  const r = Math.max(0, Math.round(((num >> 16) & 0xff) * (1 - amount)))
  const g = Math.max(0, Math.round(((num >> 8) & 0xff) * (1 - amount)))
  const b = Math.max(0, Math.round((num & 0xff) * (1 - amount)))
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`
}

// ─── Map layer — blob mode (default) ──────────────────────────────────────────

interface BlobLayerProps {
  points: HeatPoint[]
  colorMap: Map<string, string>
  hoveredSpId: string | null
  metricLabel: string
}

function BlobLayer({points, colorMap, hoveredSpId, metricLabel}: BlobLayerProps) {
  const map = useMap()
  // circles grouped by salesperson id so we can update them on hover without recreating
  const bySpRef = useRef<Map<string, L.Circle[]>>(new Map())

  useEffect(() => {
    bySpRef.current.forEach(layers => layers.forEach(l => map.removeLayer(l)))
    bySpRef.current = new Map()

    for (const p of points) {
      const {lat, lng, count, spId} = p
      if (count <= 0) continue
      const color = colorMap.get(spId) ?? '#888888'
      const radius = 6000 + Math.sqrt(count) * 2800
      const circle = L.circle([lat, lng], {
        radius,
        color: 'none',
        fillColor: color,
        fillOpacity: 0.40,
      })
        .bindTooltip(buildCustomerTooltip(p, metricLabel), {sticky: true, direction: 'auto', offset: [16, 0]})
        .addTo(map)

      if (!bySpRef.current.has(spId)) bySpRef.current.set(spId, [])
      bySpRef.current.get(spId)!.push(circle)
    }

    return () => {
      bySpRef.current.forEach(layers => layers.forEach(l => map.removeLayer(l)))
    }
  }, [map, points, colorMap, metricLabel])

  useEffect(() => {
    const anyHovered = hoveredSpId !== null
    bySpRef.current.forEach((layers, spId) => {
      const isActive = spId === hoveredSpId
      layers.forEach(l => {
        const fill = l.options.fillColor as string
        l.setStyle({
          fillOpacity: anyHovered ? (isActive ? 0.70 : 0.10) : 0.40,
          color:       isActive   ? fill  : 'none',
          weight:      isActive   ? 2     : 0,
        })
        if (isActive) l.bringToFront()
      })
    })
  }, [hoveredSpId])

  return null
}

// ─── Map layer — net mode (cluster as a "net" + individual customer dots) ────

type SpLayers = {net: L.Layer; dots: L.CircleMarker[]}

interface NetLayerProps {
  points: HeatPoint[]
  colorMap: Map<string, string>
  hoveredSpId: string | null
  metricLabel: string
}

function buildCustomerTooltip(p: HeatPoint, activeLabel: string): HTMLElement {
  const el = document.createElement('div')

  const name = document.createElement('strong')
  name.textContent = p.customerName
  el.appendChild(name)

  const location = document.createElement('div')
  location.textContent = [p.postalCode, p.city].filter(Boolean).join(' ')
  el.appendChild(location)

  const row = document.createElement('div')
  row.textContent = `${p.count.toLocaleString()} ${activeLabel}`
  el.appendChild(row)

  return el
}

function NetLayer({points, colorMap, hoveredSpId, metricLabel}: NetLayerProps) {
  const map = useMap()
  const bySpRef = useRef<Map<string, SpLayers>>(new Map())

  useEffect(() => {
    bySpRef.current.forEach(({net, dots}) => {
      map.removeLayer(net)
      dots.forEach(d => map.removeLayer(d))
    })
    bySpRef.current = new Map()

    const grouped = new Map<string, HeatPoint[]>()
    for (const p of points) {
      if (!grouped.has(p.spId)) grouped.set(p.spId, [])
      grouped.get(p.spId)!.push(p)
    }

    grouped.forEach((spPoints, spId) => {
      const color = colorMap.get(spId) ?? '#888888'
      const coords: Pt[] = spPoints.map(p => [p.lat, p.lng])

      // Build net shape depending on the number of distinct locations
      let net: L.Layer
      if (coords.length === 1) {
        net = L.circle(coords[0], {radius: 8000, color, weight: 0, fillColor: color, fillOpacity: 0.15}).addTo(map)
      } else if (coords.length === 2) {
        // No fillable area for just two points — nothing to draw beyond the dots themselves.
        net = L.polyline(coords, {color, weight: 0, opacity: 0}).addTo(map)
      } else {
        const hull = convexHull(coords)
        net = L.polygon(hull, {color, weight: 0, fillColor: color, fillOpacity: 0.15}).addTo(map)
      }

      // Draw a small dot for every customer location, with its info on hover
      const dots: L.CircleMarker[] = spPoints.map(p =>
        L.circleMarker([p.lat, p.lng], {
          radius: 3,
          color,
          weight: 1,
          fillColor: color,
          fillOpacity: 0.75,
        })
          .bindTooltip(buildCustomerTooltip(p, metricLabel), {sticky: true, direction: 'auto', offset: [16, 0]})
          .addTo(map),
      )

      bySpRef.current.set(spId, {net, dots})
    })

    return () => {
      bySpRef.current.forEach(({net, dots}) => {
        map.removeLayer(net)
        dots.forEach(d => map.removeLayer(d))
      })
    }
  }, [map, points, colorMap, metricLabel])

  useEffect(() => {
    const anyHovered = hoveredSpId !== null
    bySpRef.current.forEach(({net, dots}, spId) => {
      const isActive = spId === hoveredSpId
      const color = (net as L.Path).options.color as string

      if (net instanceof L.Polygon || net instanceof L.Circle) {
        ;(net as L.Polygon).setStyle({fillOpacity: anyHovered ? (isActive ? 0.35 : 0.05) : 0.15, weight: 0})
      } else if (net instanceof L.Polyline) {
        ;(net as L.Polyline).setStyle({opacity: 0, weight: 0})
      }
      if (isActive) (net as L.Path).bringToFront()

      dots.forEach(dot => {
        dot.setStyle({
          fillOpacity: anyHovered ? (isActive ? 1.00 : 0.20) : 0.75,
          opacity:     anyHovered ? (isActive ? 1.00 : 0.20) : 1.00,
          radius:      isActive   ? 4 : 3,
          weight:      isActive   ? 1.5 : 1,
          color:       isActive   ? darken(color) : color,
        } as L.CircleMarkerOptions)
        if (isActive) dot.bringToFront()
      })
    })
  }, [hoveredSpId])

  return null
}

// ─── Main component ───────────────────────────────────────────────────────────

interface SingleMapViewProps {
  data: SalesPersonMapData
  label?: string
  // true when this panel shares the row with a second (comparison) panel, so it needs a more
  // even map/list split; false for the normal single-map view, which gives the map most of the
  // width and keeps the list to a narrower fixed sidebar.
  compact?: boolean
  viewMode: ViewMode
  selectedManufacturers: string[]
  groupBySalesperson: boolean
  netMode: boolean
  nameFilter: string
  onNameFilterChange: (value: string) => void
  hoveredSpId: string | null
  setHoveredSpId: (id: string | null) => void
  metricLabel: string
  summaryTitle: string
}

// One side of the map: the whole map+table block for a single date. Rendered once for the normal
// (non-comparison) view, or twice side by side — sharing all filter/view-mode state, which is owned
// by the parent — when comparing two dates.
function SingleMapView({
  data, label, compact, viewMode, selectedManufacturers, groupBySalesperson, netMode,
  nameFilter, onNameFilterChange, hoveredSpId, setHoveredSpId, metricLabel, summaryTitle,
}: SingleMapViewProps) {
  const {t, i18n} = useTranslation()

  const sorted: SalesPersonMapEntry[] = useMemo(
    () => [...data.salesPersons]
      .filter(sp => sp.id !== '__unassigned__')
      .sort((a, b) => metricForEntry(b, viewMode, selectedManufacturers) - metricForEntry(a, viewMode, selectedManufacturers)),
    [data, viewMode, selectedManufacturers],
  )

  // Stable salesperson → colour mapping (order by descending metric value); a single neutral
  // colour is used for every point once salesperson grouping is switched off.
  const colorMap = useMemo(() => {
    const m = new Map<string, string>()
    if (groupBySalesperson) {
      sorted.forEach((sp, i) => m.set(sp.id, SP_COLORS[i % SP_COLORS.length]))
    } else {
      m.set(ALL_BUCKET_ID, ALL_BUCKET_COLOR)
    }
    return m
  }, [sorted, groupBySalesperson])

  // Coordinates are resolved server-side and shipped with the data — just build points for the
  // active metric. Also count customers with a positive metric value but no coordinates yet
  // (couldn't be geocoded / cache still warming) so we can tell the user instead of showing a
  // silently incomplete map.
  const {points, unplacedCount} = useMemo(() => {
    const q = groupBySalesperson ? nameFilter.trim().toLowerCase() : ''
    const result: HeatPoint[] = []
    let unplaced = 0
    for (const sp of data.salesPersons) {
      if (sp.id === '__unassigned__') continue
      if (q && !sp.lastName.toLowerCase().includes(q)) continue
      for (const c of sp.customers) {
        const value = metricForCustomer(c, viewMode, selectedManufacturers)
        if (value <= 0) continue
        if (c.lat == null || c.lng == null || !Number.isFinite(c.lat) || !Number.isFinite(c.lng)) { unplaced++; continue }
        result.push({
          lat: c.lat, lng: c.lng, count: value,
          spId: groupBySalesperson ? sp.id : ALL_BUCKET_ID,
          customerName: c.name, city: c.city, postalCode: c.postalCode,
          patientCount: c.patientCount, tmzCount: c.tmzCount,
          extSensorsCount: c.extSensorsCount, implantCount: c.implantCount,
        })
      }
    }
    return {points: result, unplacedCount: unplaced}
  }, [data, viewMode, selectedManufacturers, groupBySalesperson, nameFilter])

  // Customers with a positive metric value but no salesperson assigned are grouped under
  // '__unassigned__' and hidden from the map/table. Surface their counts so an
  // empty/partial map is explained.
  const unassignedStats = useMemo(() => {
    const u = data.salesPersons.find(sp => sp.id === '__unassigned__')
    if (!u) return {customers: 0, count: 0}
    return {
      customers: u.customers.filter(c => metricForCustomer(c, viewMode, selectedManufacturers) > 0).length,
      count: metricForEntry(u, viewMode, selectedManufacturers),
    }
  }, [data, viewMode, selectedManufacturers])

  const total = sorted.reduce((s, sp) => s + metricForEntry(sp, viewMode, selectedManufacturers), 0)

  // Flat, salesperson-independent location list shown instead of the per-salesperson table when
  // grouping is switched off — mirrors the per-salesperson table but keyed by customer location.
  const declusteredLocations = useMemo(() => {
    const result: {name: string; city: string; count: number}[] = []
    for (const sp of data.salesPersons) {
      if (sp.id === '__unassigned__') continue
      for (const c of sp.customers) {
        const count = metricForCustomer(c, viewMode, selectedManufacturers)
        if (count > 0) result.push({name: c.name, city: c.city, count})
      }
    }
    return result.sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
  }, [data, viewMode, selectedManufacturers])

  // Name search narrows whichever list is currently shown — salespeople, or customer locations
  // when ungrouped — and (via `points`, recomputed with the same inputs) the dots on the map.
  const filteredSorted = useMemo(() => {
    const q = nameFilter.trim().toLowerCase()
    if (!q) return sorted
    return sorted.filter(sp => sp.lastName.toLowerCase().includes(q))
  }, [sorted, nameFilter])

  const filteredDeclusteredLocations = useMemo(() => {
    const q = nameFilter.trim().toLowerCase()
    if (!q) return declusteredLocations
    return declusteredLocations.filter(loc => loc.name.toLowerCase().includes(q))
  }, [declusteredLocations, nameFilter])

  return (
    <Box>
      {label && (
        <Typography variant="subtitle1" sx={{fontWeight: 600, mb: 1}}>
          {label}
        </Typography>
      )}

      {data.assignmentBasis === 'current' && (
        <Typography variant="caption" color="warning.main" sx={{mb: 1.5, display: 'block'}}>
          {t('map.assignmentCurrentBasis')}
        </Typography>
      )}

      {sorted.length === 0 ? (
        <Box sx={{display: "flex", justifyContent: "center", py: 5}}>
          <Typography variant="body2" color="text.secondary" sx={{textAlign: "center"}}>
            {unassignedStats.count > 0
              ? t('map.noDataButUnassigned', {customers: unassignedStats.customers, count: unassignedStats.count, metric: metricLabel})
              : t('map.noData')}
          </Typography>
        </Box>
      ) : (
        <Box sx={{display: 'flex', gap: 2, flexWrap: 'wrap'}}>

          {/* ── Map ── */}
          <Box sx={{flex: compact ? '1 1 380px' : '3 1 560px', minWidth: 0}}>
            <MapContainer
              center={[51.2, 10.4]}
              zoom={6}
              style={{height: compact ? 420 : 500, width: '100%', borderRadius: 8}}
              scrollWheelZoom
            >
              <TileLayer
                attribution='&copy; <a href="https://www.esri.com" target="_blank">Esri</a>, HERE, Garmin, FAO, NOAA, USGS'
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
                maxZoom={16}
              />
              <TileLayer
                url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Reference/MapServer/tile/{z}/{y}/{x}"
                maxZoom={16}
              />
              {netMode && groupBySalesperson
                ? <NetLayer points={points} colorMap={colorMap} hoveredSpId={hoveredSpId} metricLabel={metricLabel}/>
                : <BlobLayer points={points} colorMap={colorMap} hoveredSpId={groupBySalesperson ? hoveredSpId : null} metricLabel={metricLabel}/>
              }
            </MapContainer>

            <Typography variant="caption" color="text.secondary" sx={{mt: 0.5, display: 'block'}}>
              {t('map.sizeHint')}
            </Typography>
            {unplacedCount > 0 && (
              <Typography variant="caption" color="warning.main" sx={{mt: 0.5, display: 'block'}}>
                {t('map.someUnplaced', {count: unplacedCount})}
              </Typography>
            )}
            {unassignedStats.count > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{mt: 0.5, display: 'block'}}>
                {t('map.unassignedNote', {customers: unassignedStats.customers, count: unassignedStats.count, metric: metricLabel})}
              </Typography>
            )}
          </Box>

          {/* ── Summary table ── */}
          <Box sx={{flex: compact ? '1 1 260px' : '0 0 280px', minWidth: compact ? 220 : 240}}>
            <Typography variant="subtitle2" sx={{fontWeight: 600, mb: 1}}>
              {summaryTitle}
            </Typography>
            <TextField
              size="small"
              fullWidth
              placeholder={t('common.search')}
              value={nameFilter}
              onChange={e => onNameFilterChange(e.target.value)}
              sx={{mb: 1}}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small"/></InputAdornment>,
                },
              }}
            />
            <Paper variant="outlined" sx={{maxHeight: compact ? 420 : 500, overflowY: 'auto', overflowX: 'hidden'}}>
              <Table size="small" stickyHeader>
                {groupBySalesperson ? (
                  <>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{fontWeight: 600}}>{t('map.salesperson')}</TableCell>
                        <TableCell align="right" sx={{fontWeight: 600}}>{metricLabel}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSorted.map((sp) => {
                        const color = colorMap.get(sp.id) ?? '#888888'
                        const isHovered = hoveredSpId === sp.id
                        const count = metricForEntry(sp, viewMode, selectedManufacturers)
                        return (
                          <TableRow
                            key={sp.id}
                            onMouseEnter={() => setHoveredSpId(sp.id)}
                            onMouseLeave={() => setHoveredSpId(null)}
                            sx={{
                              cursor: 'pointer',
                              backgroundColor: isHovered ? `${color}18` : undefined,
                              transition: 'background-color 0.15s',
                            }}
                          >
                            <TableCell sx={{fontSize: 13}}>
                              <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
                                {/* Colour dot — same as on the map */}
                                <Box sx={{
                                  width: 12, height: 12, borderRadius: '50%',
                                  backgroundColor: color,
                                  flexShrink: 0,
                                  border: '2px solid',
                                  borderColor: isHovered ? color : 'transparent',
                                  boxShadow: isHovered ? `0 0 0 2px ${color}40` : 'none',
                                  transition: 'box-shadow 0.15s',
                                }}/>
                                <Box>
                                  <span>{sp.lastName}</span>
                                </Box>
                              </Box>
                            </TableCell>
                            <TableCell align="right" sx={{fontWeight: 600, fontSize: 13}}>
                              {count.toLocaleString(i18n.language)}
                            </TableCell>
                          </TableRow>
                        )
                      })}
                      <TableRow>
                        <TableCell sx={{fontWeight: 600, pl: '36px'}}>{t('map.total')}</TableCell>
                        <TableCell align="right" sx={{fontWeight: 700}}>
                          {total.toLocaleString(i18n.language)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </>
                ) : (
                  <>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{fontWeight: 600}}>{t('map.customer')}</TableCell>
                        <TableCell align="right" sx={{fontWeight: 600}}>{metricLabel}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredDeclusteredLocations.map((loc, i) => (
                        <TableRow key={`${loc.name}-${loc.city}-${i}`}>
                          <TableCell sx={{fontSize: 13}}>
                            <span>{loc.name}</span>
                            <Typography component="div" variant="caption" color="text.secondary">
                              {loc.city}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{fontWeight: 600, fontSize: 13}}>
                            {loc.count.toLocaleString(i18n.language)}
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow>
                        <TableCell sx={{fontWeight: 600, pl: '36px'}}>{t('map.total')}</TableCell>
                        <TableCell align="right" sx={{fontWeight: 700}}>
                          {total.toLocaleString(i18n.language)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </>
                )}
              </Table>
            </Paper>
          </Box>

        </Box>
      )}
    </Box>
  )
}

interface Props {
  data: SalesPersonMapData
  compareData?: SalesPersonMapData | null
}

export default function SalesPersonMap({data, compareData}: Props) {
  const {t, i18n} = useTranslation()

  const [viewMode, setViewMode] = useState<ViewMode>('patients')
  const [selectedManufacturers, setSelectedManufacturers] = useState<string[]>([...IMPLANT_MANUFACTURERS])
  const [groupBySalesperson, setGroupBySalesperson] = useState(true)
  const [netMode, setNetMode] = useState(false)
  const [nameFilter, setNameFilter] = useState('')
  const [hoveredSpId, setHoveredSpId] = useState<string | null>(null)
  const [filtersAnchor, setFiltersAnchor] = useState<HTMLElement | null>(null)

  const isInComparisonMode = !!compareData

  // The search box filters salespeople in grouped mode and customer names in declustered mode —
  // a stale query from one mode would silently return zero results in the other.
  React.useEffect(() => { setNameFilter('') }, [groupBySalesperson])

  const metricLabel = viewMode === 'patients' ? t('map.patients')
    : viewMode === 'tmz' ? t('map.tmz')
    : viewMode === 'extSensors' ? t('map.viewModeExtSensors')
    : t('map.viewModeImplants')

  const summaryTitle = groupBySalesperson
    ? (viewMode === 'patients' ? t('map.summaryTitle')
      : viewMode === 'tmz' ? t('map.summaryTitleTmz')
      : viewMode === 'extSensors' ? t('map.summaryTitleExtSensors')
      : t('map.summaryTitleImplants'))
    : (viewMode === 'patients' ? t('map.summaryTitleAll')
      : viewMode === 'tmz' ? t('map.summaryTitleTmzDeclustered')
      : viewMode === 'extSensors' ? t('map.summaryTitleExtSensorsDeclustered')
      : t('map.summaryTitleImplantsDeclustered'))

  // A short, locale-aware label for a panel's date, shown above its map so the two sides of a
  // comparison stay identifiable independently of the date pickers above.
  const formatPanelDate = (isoDate: string) => {
    const d = dayjs(isoDate)
    return i18n.language.startsWith('de')
      ? d.locale('de').format('D. MMMM YYYY')
      : d.format('MMMM D, YYYY')
  }

  const sharedPanelProps = {
    viewMode, selectedManufacturers, groupBySalesperson, netMode,
    nameFilter, onNameFilterChange: setNameFilter,
    hoveredSpId, setHoveredSpId, metricLabel, summaryTitle,
  }

  return (
    <Box>
      {/* ── View mode tabs (left) / more-filters dropdown (right) ── */}
      <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5, mb: 1.5}}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          size="small"
          onChange={(_e, val) => { if (val) setViewMode(val) }}
        >
          <ToggleButton value="patients">{t('map.viewModePatients')}</ToggleButton>
          <ToggleButton value="tmz">{t('map.viewModeTmz')}</ToggleButton>
          <ToggleButton value="extSensors">{t('map.viewModeExtSensors')}</ToggleButton>
          <ToggleButton value="implants">{t('map.viewModeImplants')}</ToggleButton>
        </ToggleButtonGroup>

        <Button
          size="small"
          variant="outlined"
          startIcon={<FilterListIcon/>}
          onClick={e => setFiltersAnchor(e.currentTarget)}
        >
          {t('map.moreFilters')}
        </Button>

        <Popover
          open={Boolean(filtersAnchor)}
          anchorEl={filtersAnchor}
          onClose={() => setFiltersAnchor(null)}
          anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
          transformOrigin={{vertical: 'top', horizontal: 'right'}}
        >
          <Box sx={{p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 260}}>
            <FormControlLabel
              control={
                <Switch
                  checked={groupBySalesperson}
                  onChange={e => setGroupBySalesperson(e.target.checked)}
                  size="small"
                />
              }
              label={<Typography variant="body2">{t('map.groupBySalesperson')}</Typography>}
            />

            {groupBySalesperson && (
              <FormControlLabel
                control={
                  <Switch
                    checked={netMode}
                    onChange={e => setNetMode(e.target.checked)}
                    size="small"
                  />
                }
                label={<Typography variant="body2">{t('map.netToggle')}</Typography>}
              />
            )}

            {viewMode === 'implants' && (
              <FormControl size="small" fullWidth>
                <InputLabel id="map-manufacturers-label">{t('map.selectManufacturers')}</InputLabel>
                <Select
                  labelId="map-manufacturers-label"
                  label={t('map.selectManufacturers')}
                  multiple
                  value={selectedManufacturers}
                  onChange={e => {
                    const val = e.target.value
                    setSelectedManufacturers(typeof val === 'string' ? val.split(',') : val)
                  }}
                  renderValue={selected => (
                    <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 0.5}}>
                      {(selected as string[]).map(mfr => <Chip key={mfr} label={mfr} size="small"/>)}
                    </Box>
                  )}
                >
                  {IMPLANT_MANUFACTURERS.map(mfr => (
                    <MenuItem key={mfr} value={mfr}>
                      <Checkbox size="small" checked={selectedManufacturers.includes(mfr)}/>
                      <ListItemText primary={mfr}/>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Popover>
      </Box>

      {viewMode === 'extSensors' && (
        <Typography variant="caption" color="text.secondary" sx={{mb: 1, display: 'block', fontStyle: 'italic'}}>
          {t('map.extSensorsManufacturerNote')}
        </Typography>
      )}

      {isInComparisonMode ? (
        <Box sx={{display: 'flex', gap: 3, flexWrap: 'wrap'}}>
          <Box sx={{flex: '1 1 460px', minWidth: 380}}>
            <SingleMapView data={data} label={formatPanelDate(data.date)} compact {...sharedPanelProps}/>
          </Box>
          <Box sx={{flex: '1 1 460px', minWidth: 380}}>
            <SingleMapView data={compareData} label={formatPanelDate(compareData.date)} compact {...sharedPanelProps}/>
          </Box>
        </Box>
      ) : (
        <SingleMapView data={data} label={formatPanelDate(data.date)} {...sharedPanelProps}/>
      )}
    </Box>
  )
}
